import { FC, useMemo } from 'react'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts'

import { PerformanceType } from '@pages/IntroducingBroker/Dashboard/QuickPerformance/TypeFilter'

import { useArabicSessionLanguage } from '@context/SessionSettingsContext'

import { useWindowResize } from '@utils/domUtils'

import { CustomChartTooltip } from './CustomChartTooltip'

export interface ChartDataItem {
  name: string
  tooltipLabel?: string
  value: number
}

interface ChartProps {
  data: ChartDataItem[]
  performanceType: PerformanceType
}

export const Chart: FC<ChartProps> = (props) => {
  const { data, performanceType } = props

  const isArabic = useArabicSessionLanguage()
  const isMobile = useWindowResize()
  const ONE_WEEK_DAYS = 7
  const TWO_WEEKS_DAYS = 14

  const chartData = useMemo<ChartDataItem[]>(() => {
    return isArabic ? data.slice().reverse() : data
  }, [data, isArabic])

  const color = useMemo<string>(() => {
    switch (performanceType) {
      case PerformanceType.Commission:
        return 'var(--outline-warning-high-em)'
      case PerformanceType.Lots:
        return 'var(--outline-success-high-em)'
      case PerformanceType.Clients:
        return 'var(--outline-info-high-em)'
      default:
        return 'var(--outline-low-em)'
    }
  }, [performanceType])

  const interval = useMemo(() => {
    if (!isMobile) return undefined
    const len = chartData.length
    if (len <= ONE_WEEK_DAYS) return 1
    if (len <= TWO_WEEKS_DAYS) return 2
    return Math.ceil(len / 4)
  }, [isMobile, chartData])

  return (
    <ResponsiveContainer width='100%' height='100%'>
      <LineChart data={chartData} style={{ direction: isArabic ? 'rtl' : 'ltr' }}>
        <CartesianGrid
          strokeDasharray='12 12'
          horizontal
          vertical={false}
          stroke='var(--outline-low-em)'
        />
        <XAxis
          fontSize={chartData.length > 10 ? 10 : 12}
          axisLine={false}
          interval={interval}
          dataKey='name'
          padding={{ right: 20 }}
          dy={18}
          tick={{ fill: 'var(--text-low-em)' }}
          tickLine={false}
        />
        <YAxis
          orientation={isArabic ? 'right' : 'left'}
          axisLine={false}
          tickLine={false}
          dx={isArabic ? 52 : -52}
          fontSize={12}
          textAnchor='start'
          tick={{ fill: 'var(--text-low-em)' }}
        />
        <Tooltip
          cursor={false}
          content={(props: TooltipProps<number, string>) => (
            <CustomChartTooltip
              {...props}
              performanceType={performanceType}
              labelFormatter={(label) => {
                const dataPoint = chartData.find((d) => d.name === label)
                return dataPoint?.tooltipLabel || label
              }}
            />
          )}
        />
        <Line
          type='monotone'
          dataKey='value'
          stroke={color}
          strokeWidth={3}
          dot={false}
          activeDot={{ r: 6, stroke: color, fill: 'var(--surface-white-smoke-4)', strokeWidth: 4 }}
        />
      </LineChart>
    </ResponsiveContainer>
  )
}
